import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon,
  ExclamationTriangleIcon,
  BeakerIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import apiClient from '../../utils/api';

interface CostAnalysisData {
  pond_id: string;
  pond_name: string;
  total_feed_cost: number;
  total_medicine_cost: number;
  total_direct_costs: number;
  total_allocated_location_costs: number;
  total_location_expenses: number;
  allocation_proportion: number;
  total_allocated_costs: number;
  cost_per_kg: number;
  feed_efficiency: number;
  cost_breakdown: {
    feed: number;
    medicine: number;
    direct: number;
    allocated_location: number;
    total: number;
  };
}

const CostAnalysis: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [analyses, setAnalyses] = useState<CostAnalysisData[]>([]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  useEffect(() => {
    // Set default date range (January 1st of current year to today)
    const today = new Date();
    const currentYear = today.getFullYear();
    const januaryFirst = new Date(`${currentYear}-01-01T00:00:00+05:30`);
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    setEndDate(formatDate(today));
    setStartDate(formatDate(januaryFirst));
  }, []);

  useEffect(() => {
    if (selectedLocation && startDate && endDate) {
      fetchCostAnalysis();
    }
  }, [selectedLocation]);

  const fetchCostAnalysis = async () => {
    if (!selectedLocation || !startDate || !endDate) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/reports/cost-analysis', {
        params: {
          location_id: selectedLocation.id,
          start: startDate,
          end: endDate,
        },
      });
      setAnalyses(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load cost analysis');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const getEfficiencyColor = (efficiency: number) => {
    if (efficiency <= 2) return 'text-green-600';
    if (efficiency <= 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getEfficiencyLabel = (efficiency: number) => {
    if (efficiency <= 2) return 'Excellent';
    if (efficiency <= 3) return 'Good';
    if (efficiency <= 4) return 'Fair';
    return 'Poor';
  };

  const totalFeedCost = analyses.reduce((sum, a) => sum + a.total_feed_cost, 0);
  const totalMedicineCost = analyses.reduce((sum, a) => sum + a.total_medicine_cost, 0);
  const totalDirectCost = analyses.reduce((sum, a) => sum + a.total_direct_costs, 0);
  // const totalAllocatedCost = analyses.reduce((sum, a) => sum + a.total_allocated_costs, 0);
  const averageFeedEfficiency = analyses.length > 0 
    ? analyses.reduce((sum, a) => sum + a.feed_efficiency, 0) / analyses.length 
    : 0;

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <BeakerIcon className="h-16 w-16 text-blue-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Cost Analysis</h1>
        <p className="text-lg text-gray-600">
          Detailed cost breakdown and feed efficiency analysis
        </p>
      </div>

      {/* Date Range Selector + Action */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchCostAnalysis}
              disabled={loading || !startDate || !endDate}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <CalendarIcon className="h-4 w-4" />
              {loading ? 'Loading...' : 'Generate Analysis'}
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Summary Cards */}
      {analyses.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center">
            <div className="text-2xl font-bold text-orange-600">
              {formatCurrency(totalFeedCost)}
            </div>
            <div className="text-sm text-gray-600">Total Feed Cost</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center">
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(totalMedicineCost)}
            </div>
            <div className="text-sm text-gray-600">Total Medicine Cost</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(totalDirectCost)}
            </div>
            <div className="text-sm text-gray-600">Total Direct Cost</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center">
            <div className="text-2xl font-bold text-purple-600">
              {formatCurrency(analyses[0]?.total_location_expenses || 0)}
            </div>
            <div className="text-sm text-gray-600">Total Location Costs</div>
            <div className="text-xs text-gray-500">Shared infrastructure</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center">
            <div className="text-2xl font-bold text-green-600">
              {averageFeedEfficiency.toFixed(2)}
            </div>
            <div className="text-sm text-gray-600">Avg Feed Efficiency</div>
          </div>
        </div>
      )}

      {/* Cost Analysis Table */}
      {analyses.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Pond Cost Analysis</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pond
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Feed Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Medicine Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Direct Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Allocation %
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cost per Kg
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Feed Efficiency
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {analyses.map((analysis, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {analysis.pond_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600">
                      {formatCurrency(analysis.total_feed_cost)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                      {formatCurrency(analysis.total_medicine_cost)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                      {formatCurrency(analysis.total_direct_costs)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-600">
                      <div>
                        <div className="font-medium">{formatCurrency(analysis.total_allocated_location_costs)}</div>
                        <div className="text-xs text-gray-500">of {formatCurrency(analysis.total_location_expenses)}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <span className="font-medium">{analysis.allocation_proportion.toFixed(1)}%</span>
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-purple-600 h-2 rounded-full" 
                            style={{ width: `${Math.min(analysis.allocation_proportion, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {formatCurrency(analysis.cost_breakdown.total)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(analysis.cost_per_kg)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${getEfficiencyColor(analysis.feed_efficiency)}`}>
                          {analysis.feed_efficiency.toFixed(2)}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full ${getEfficiencyColor(analysis.feed_efficiency)} bg-opacity-20`}>
                          {getEfficiencyLabel(analysis.feed_efficiency)}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cost Breakdown Charts */}
      {analyses.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Feed vs Medicine Cost */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Feed vs Medicine Costs</h3>
            <div className="space-y-3">
              {analyses.map((analysis, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-900">{analysis.pond_name}</span>
                    <span className="text-gray-600">
                      {formatCurrency(analysis.total_feed_cost + analysis.total_medicine_cost)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="flex h-2 rounded-full">
                      <div 
                        className="bg-orange-500 h-2 rounded-l-full"
                        style={{ 
                          width: `${(analysis.total_feed_cost / (analysis.total_feed_cost + analysis.total_medicine_cost)) * 100}%` 
                        }}
                      ></div>
                      <div 
                        className="bg-red-500 h-2 rounded-r-full"
                        style={{ 
                          width: `${(analysis.total_medicine_cost / (analysis.total_feed_cost + analysis.total_medicine_cost)) * 100}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Feed: {formatCurrency(analysis.total_feed_cost)}</span>
                    <span>Medicine: {formatCurrency(analysis.total_medicine_cost)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Feed Efficiency Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Feed Efficiency by Pond</h3>
            <div className="space-y-3">
              {analyses.map((analysis, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-900">{analysis.pond_name}</span>
                    <span className={`font-medium ${getEfficiencyColor(analysis.feed_efficiency)}`}>
                      {analysis.feed_efficiency.toFixed(2)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${getEfficiencyColor(analysis.feed_efficiency).replace('text-', 'bg-')}`}
                      style={{ 
                        width: `${Math.min((analysis.feed_efficiency / 5) * 100, 100)}%` 
                      }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {getEfficiencyLabel(analysis.feed_efficiency)} efficiency
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* No Data Message */}
      {analyses.length === 0 && !loading && (
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <BeakerIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Cost Data Available</h3>
          <p className="text-gray-600">
            Cost analysis will be available once you have expense and feed data.
          </p>
        </div>
      )}
    </div>
  );
};

export default CostAnalysis;
